/*
 * Which RMS sign-in page a person belongs on (TDI-702, TDI-704).
 *
 * School staff sign in with a password on /rms/school-sign-in, parents and
 * carers with an emailed link on /rms/family/sign-in, and anyone else keeps
 * the older /rms/sign-in page. These helpers turn the address a person was
 * heading for (a `continue` path, or the continueUrl an emailed link
 * carries) into the right sign-in page. Pure functions, no lookups: they
 * never reveal whether an account exists.
 */

export const SCHOOL_SIGN_IN = '/rms/school-sign-in';
export const FAMILY_SIGN_IN = '/rms/family/sign-in';
export const LEGACY_SIGN_IN = '/rms/sign-in';

// Pages only school staff use: the SENCO portal and a class teacher's pupils.
const SCHOOL_PAGES = ['/rms/pupils', '/rms/teacher'];
const FAMILY_PAGES = ['/rms/family/'];

// '/rms/pupils' matches itself and anything under it ('/rms/pupils/PR-1',
// '/rms/pupils?x=1'), but not '/rms/pupilsfoo'. A prefix ending in '/'
// matches anything under it.
function startsWithPage(path, prefixes) {
  return prefixes.some((prefix) => (
    prefix.endsWith('/')
      ? path.startsWith(prefix)
      : path === prefix || path.startsWith(`${prefix}/`) || path.startsWith(`${prefix}?`)
  ));
}

/** 'school', 'family' or null for a continue path. */
export function audienceForContinue(continuePath) {
  const path = String(continuePath || '');
  if (startsWithPage(path, SCHOOL_PAGES)) return 'school';
  if (startsWithPage(path, FAMILY_PAGES)) return 'family';
  return null;
}

/**
 * The sign-in page (with `continue` kept) that a continue path belongs on,
 * or null when it belongs on the page it is already on.
 */
export function signInPageForContinue(continuePath) {
  const audience = audienceForContinue(continuePath);
  if (!audience) return null;
  const page = audience === 'school' ? SCHOOL_SIGN_IN : FAMILY_SIGN_IN;
  return `${page}?continue=${encodeURIComponent(continuePath)}`;
}

/**
 * Parse an emailed link's continueUrl into a same-origin path, or null when
 * it is missing, unreadable, on another site or outside /rms/.
 */
export function rmsPathFromContinueUrl(continueUrl, origin) {
  if (!continueUrl) return null;
  try {
    const base = origin || window.location.origin;
    const url = new URL(continueUrl, base);
    if (url.origin !== new URL(base).origin) return null;
    if (!url.pathname.startsWith('/rms/')) return null;
    return url;
  } catch (_) {
    return null;
  }
}

/**
 * Where a failed or used emailed link sends the person: the sign-in page for
 * the address it was heading to. School pages go to the school sign-in
 * page, family pages to the family one, anything else to /rms/sign-in.
 */
export function signInPageForFailedLink(continueUrl, origin) {
  const url = rmsPathFromContinueUrl(continueUrl, origin);
  if (!url) return LEGACY_SIGN_IN;
  // Emailed sign-in links point at /rms/sign-in carrying where to go next.
  if (url.pathname === LEGACY_SIGN_IN) {
    return signInPageForContinue(url.searchParams.get('continue')) || LEGACY_SIGN_IN;
  }
  // A link that already names its sign-in page (the school or family page,
  // the provider page) goes back to it.
  if (url.pathname.endsWith('sign-in')) return url.pathname;
  return signInPageForContinue(url.pathname) || LEGACY_SIGN_IN;
}

/**
 * After a new password is set from an emailed reset link: the link's own
 * /rms/ continue address, with the "password is set" banner and the email
 * filled in. Null when the link does not point into RMS, so other products
 * keep their own behaviour.
 */
export function resetCompletePath(continueUrl, email, origin) {
  const url = rmsPathFromContinueUrl(continueUrl, origin);
  if (!url) return null;
  const params = new URLSearchParams(url.search);
  params.set('message', 'setup_complete');
  if (email) {
    try {
      params.set('e', btoa(email));
    } catch (_) {
      // An address btoa cannot encode is simply not filled in.
    }
  }
  return `${url.pathname}?${params.toString()}`;
}
